import React, { useEffect, useRef, useState } from "react";
import { Container, Text, BitmapText } from "@pixi/react";
import BasicTexture from "../../../Elements/Textures/BasicTexture";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  animateTextCounter,
  calculateFontSize,
  formatToCustomLocale,
  getWinDuration,
} from "../../../utilities";
import anime from "animejs";
import SpineAnimation from "../../../Elements/Textures/SpineAnimation";
import { ASSET_NAME } from "../../../assets";
import { AnimationSettings } from "../../../settings";
import { setCounterAnimationFinished } from "../../../State/Slices/spinSlice";

// Functional component WinsDisplay
const WinsDisplay = ({ width, height, x, y, zIndex, dimensions }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const counterRef = useRef(null);
  const cancelAnimation = useRef(null);
  const displayBackgroundRef = useRef(null);

  const [displayedText, setDisplayedText] = useState(
    t("spin_to_win").toUpperCase()
  );
  const [fontSize, setFontSize] = useState(
    calculateFontSize(dimensions.width, 28, 20)
  );
  const [styleKey, setStyleKey] = useState(0);

  // Redux selectors
  const { isSpinning, spinComplete } = useSelector((state) => state.spin);
  const { currency, winType, currentRound } = useSelector(
    (state) => state.game
  ); // ✅ Use `currentRound`
  const { freeSpinActive } = useSelector((state) => state.freeSpins);

  // Effect to update font size based on dimensions
  useEffect(() => {
    const newFontSize = calculateFontSize(dimensions.width, 28, 20);
    if (newFontSize !== fontSize) {
      setFontSize(newFontSize);
      setStyleKey((prev) => prev + 1); // Update style key to force re-render
    }
  }, [dimensions.width, fontSize]);

  // Effect to animate the background element
  useEffect(() => {
    if (displayBackgroundRef.current) {
      const animation = anime({
        targets: displayBackgroundRef.current,
        y: [
          { value: `+=4`, duration: 1500, easing: "easeInOutSine" },
          { value: `-=4`, duration: 1500, easing: "easeInOutSine" },
        ],
        loop: true,
        direction: "alternate",
      });

      return () => animation.pause(); // Cleanup to pause animation
    }
  }, []);

  // Effect to handle text display and counter animation
  useEffect(() => {
    if (freeSpinActive) return;

    if (isSpinning) {
      if (cancelAnimation.current) {
        cancelAnimation.current();
        cancelAnimation.current = null;
      }
      setDisplayedText(t("good_luck").toUpperCase());
    } else {
      setDisplayedText(t("spin_to_win").toUpperCase());
    }

    if (currentRound?.win > 0 && spinComplete) {
      // ✅ Use `currentRound`
      dispatch(setCounterAnimationFinished(false));

      cancelAnimation.current = animateTextCounter(
        counterRef,
        0,
        currentRound?.win, // ✅ Use `currentRound`
        winType
          ? getWinDuration(winType)
          : AnimationSettings.INCREMENT_DURATION,
        (value) => {
          const text = `${formatToCustomLocale(
            value
          )} ${currency.toUpperCase()}`;
          setDisplayedText(text);
          return text;
        },
        () => {
          setTimeout(() => {
            dispatch(setCounterAnimationFinished(true));
          }, 1000);
        }
      );
    }

    return () => {
      if (cancelAnimation.current) {
        cancelAnimation.current();
        cancelAnimation.current = null;
      }
    };
  }, [
    isSpinning,
    currentRound, // ✅ Updated
    currency,
    spinComplete,
    winType,
    dispatch,
    freeSpinActive,
  ]);

  // Effect to update displayed text based on language and game state
  useEffect(() => {
    setDisplayedText(() => {
      if (isSpinning) return t("good_luck").toUpperCase();
      if (currentRound?.win && spinComplete) {
        // ✅ Use `currentRound`
        return `${formatToCustomLocale(
          currentRound?.win
        )} ${currency.toUpperCase()}`;
      }
      return t("spin_to_win").toUpperCase();
    });
  }, [t, i18n.language, isSpinning, currentRound, currency, spinComplete]); // ✅ Updated dependencies

  return (
    <Container name="WinsDisplay" x={x} y={y} zIndex={zIndex}>
      <BasicTexture
        ref={displayBackgroundRef}
        textureName="reelBottom"
        width={width}
        height={height}
        zIndex={2}
      />

      <SpineAnimation
        animationName="Stone3"
        x={-y}
        y={0}
        zIndex={2}
        animationPath={ASSET_NAME.background}
      />
      <SpineAnimation
        animationName="Stone"
        x={0}
        y={y / 4.5}
        width={width / 4}
        height={height / 2}
        zIndex={2}
        animationPath={ASSET_NAME.background}
      />
      <SpineAnimation
        animationName="Stone2"
        x={y}
        y={0}
        zIndex={2}
        animationPath={ASSET_NAME.background}
      />

      {!freeSpinActive && (
        <>
          {winType ? (
            <BitmapText
              zIndex={5}
              key={styleKey}
              ref={counterRef}
              text={displayedText}
              anchor={0.5}
              style={{
                fontName: `jackpot-diamond`,
                fontSize: fontSize * 2,
                fill: "#ffffff",
                fontWeight: "bold",
                align: "center",
              }}
            />
          ) : (
            <Text
              zIndex={5}
              key={styleKey}
              ref={counterRef}
              text={displayedText}
              anchor={0.5}
              style={{
                fontSize,
                fill: "#ffffff",
                fontWeight: "bold",
                align: "center",
              }}
            />
          )}
        </>
      )}
    </Container>
  );
};

export default WinsDisplay;
